/**
 * Section Manager Component
 * Manages room/section creation and assignment for quotation items
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { FolderPlus, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { PRESET_SECTIONS } from './constants';

export function SectionManager({ 
  sections, 
  setSections, 
  setItemSections,
  showAddSection,
  setShowAddSection
}) {
  const [newSectionName, setNewSectionName] = useState('');

  const handleAddSection = () => {
    if (!newSectionName.trim()) {
      toast.error('Section name is required');
      return;
    }
    const sectionToAdd = newSectionName.trim();
    if (sections.includes(sectionToAdd)) {
      toast.error('Section already exists');
      return;
    }
    // When adding first section, also add "General" for unassigned items
    if (sections.length === 0) {
      setSections([sectionToAdd, 'General']);
    } else {
      setSections([...sections, sectionToAdd]);
    }
    setNewSectionName('');
    setShowAddSection(false);
    toast.success(`Added section: ${sectionToAdd}`);
  };

  const handleRemoveSection = (sectionName) => {
    setSections(sections.filter(s => s !== sectionName));
    // Remove section assignment from items
    setItemSections(prev => {
      const newItemSections = { ...prev };
      Object.keys(newItemSections).forEach(key => {
        if (newItemSections[key] === sectionName) {
          delete newItemSections[key];
        }
      });
      return newItemSections;
    });
    toast.success(`Removed section: ${sectionName}`);
  };

  return (
    <>
      {/* Sections Display */}
      {sections.length > 0 && (
        <div className="mt-3 p-2 bg-heda-cream/60 rounded-xl border border-heda-line">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-heda-sub font-medium">Sections:</span>
            {sections.map(section => (
              <Badge key={section} variant="outline" className="rounded-full text-xs bg-white border-heda-gold text-heda-gold pr-1">
                {section}
                <button onClick={() => handleRemoveSection(section)} className="ml-1 hover:text-red-700">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            <Button variant="ghost" size="sm" onClick={() => setShowAddSection(true)} className="rounded-xl h-6 text-xs text-heda-sub">
              <Plus className="w-3 h-3" /> Add
            </Button>
          </div>
          <p className="text-[10px] text-heda-mute mt-1">Tip: Assign items to sections using the dropdown in each row</p>
        </div>
      )}

      {/* Add Section Dialog */}
      <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
        <DialogContent className="border-heda-line bg-white sm:rounded-3xl sm:max-w-sm" data-testid="add-section-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="w-5 h-5 text-heda-gold" /> Add Room/Section
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="heda-label">Section Name</label>
              <Input
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                placeholder="e.g., Living Room 1, Kitchen..."
                className="rounded-xl bg-heda-cream/50 mt-1"
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddSection(); }}
                data-testid="section-name-input"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {PRESET_SECTIONS.map(preset => (
                <Button 
                  key={preset} 
                  variant="outline" 
                  size="sm" 
                  className="rounded-xl text-xs h-6 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold" 
                  onClick={() => setNewSectionName(preset)}
                >
                  {preset}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowAddSection(false); setNewSectionName(''); }} className="rounded-xl flex-1 h-9 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold">
                Cancel
              </Button>
              <Button onClick={handleAddSection} className="rounded-xl flex-1 bg-heda-gold hover:bg-heda-gold/90 text-white h-9" data-testid="confirm-add-section-btn">
                <Plus className="w-3 h-3 mr-1" />Add Section
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SectionManager;
